import {
  PLAYER_LABEL,
  REPLAY_TEXT_LABEL,
  journalDay,
  journalDiffs,
  journalPlayer,
  replacedSentence,
  replayCountSentence,
  seatSummary,
} from '@/lib/replay-settings';
import type { ReplaySettingsChange } from '@matter_extractor/shared';
import { useId, useState } from 'react';

/**
 * One line of « Ce qui a déjà bougé » (docs/specs/rejeu.md §10.3): a sentence,
 * and the way to open what it replaced.
 *
 * ## A sentence, as in Réglages > Modèles
 *
 * « 2026-10-14 alice a remplacé « Règles communes » et « Mode complété », à 1
 * rejeu déjà joué. Motif : ... ». The date is the calendar day, the author is
 * the operator who saved, the texts are the ones the save replaced, and the
 * count is how many replays had been played at that moment - the figure that
 * tells a calibration from a change of instrument. The reason is written only
 * when there is one: an empty « Motif : » would read as something the screen
 * failed to fill in.
 *
 * ## The seed has its own sentence
 *
 * The starting texts are written by the server, not by a person: no author, no
 * « a remplacé ». It says what happened, when, and at how many replays - which
 * is also what a refresh of the dev model writes when it puts them back.
 *
 * ## The model of the player is a line of its own kind (npc.md §13.4, H28)
 *
 * « a remplacé « Modèle du joueur » (Claude Haiku 5.5 vers Claude Sonnet 5.5) » - named like a text,
 * with what moved after it. Its before and after open with the texts', each as a few lines: the
 * model, the pin as the settings write it, and the tariff.
 *
 * ## « Voir l'avant et l'après » opens ON THE SPOT
 *
 * A disclosure under the sentence, not a dialog and not another page: the
 * operator is comparing two paragraphs and a modal would hide the field they
 * are about to rewrite. Each replaced text is drawn before and after, side by
 * side from `md` up; a seed line has no « avant », so it opens on the texts as
 * they were sown.
 */

export interface ReplayJournalEntryProps {
  readonly change: ReplaySettingsChange;
}

export function ReplayJournalEntry({ change }: ReplayJournalEntryProps) {
  const [open, setOpen] = useState(false);
  const detailId = useId();
  const seeded = change.kind === 'seed';
  const diffs = journalDiffs(change);
  const player = journalPlayer(change);

  return (
    <li
      data-testid="rejeu-journal-ligne"
      data-kind={change.kind}
      className="rounded-lg border border-gray-200 bg-white p-3 text-gray-800 text-small leading-relaxed"
    >
      <span className="text-gray-600">{journalDay(change)}</span>{' '}
      {seeded ? (
        <>
          Les textes de départ ont été semés au premier démarrage du serveur, à{' '}
          {replayCountSentence(change.replayCountAtChange)}.
        </>
      ) : (
        <>
          <strong className="font-semibold text-bleu-nuit">{change.changedBy}</strong> a remplacé{' '}
          {replacedSentence(change.replaced, player)}, à{' '}
          {replayCountSentence(change.replayCountAtChange)}.
        </>
      )}
      {change.note === null ? null : (
        <span data-testid="rejeu-journal-motif">
          {' '}
          Motif : {change.note}
          {/[.!?…]$/.test(change.note) ? '' : '.'}
        </span>
      )}{' '}
      <button
        type="button"
        data-testid="rejeu-journal-voir"
        aria-expanded={open}
        aria-controls={detailId}
        onClick={() => setOpen((was) => !was)}
        className="cursor-pointer font-semibold text-bleu-nuit underline underline-offset-2"
      >
        {open ? 'Masquer' : seeded ? 'Voir' : 'Voir l’avant et l’après'}
      </button>
      {open ? (
        <div id={detailId} data-testid="rejeu-journal-detail" className="mt-3 flex flex-col gap-4">
          {player === null || player.after === null ? null : (
            <div data-testid="rejeu-journal-joueur">
              <p className="font-semibold text-gray-900">{PLAYER_LABEL}</p>
              <div className={`mt-1 grid gap-3 ${player.before === null ? '' : 'md:grid-cols-2'}`}>
                {player.before === null ? null : (
                  <ReplayText
                    label="Avant"
                    testId="rejeu-journal-joueur-avant"
                    text={seatSummary(player.before)}
                  />
                )}
                <ReplayText
                  label="Après"
                  testId="rejeu-journal-joueur-apres"
                  text={seatSummary(player.after)}
                />
              </div>
            </div>
          )}
          {diffs.map((diff) => (
            <div key={diff.key} data-testid="rejeu-journal-texte" data-key={diff.key}>
              <p className="font-semibold text-gray-900">{REPLAY_TEXT_LABEL[diff.key]}</p>
              <div className={`mt-1 grid gap-3 ${diff.before === null ? '' : 'md:grid-cols-2'}`}>
                {diff.before === null ? null : (
                  <ReplayText label="Avant" testId="rejeu-journal-avant" text={diff.before} />
                )}
                <ReplayText
                  label={diff.before === null ? 'Texte semé' : 'Après'}
                  testId="rejeu-journal-apres"
                  text={diff.after}
                />
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </li>
  );
}

/** One text, whole, in a box that scrolls: the paragraphs keep their line breaks. */
function ReplayText({
  label,
  testId,
  text,
}: {
  readonly label: string;
  readonly testId: string;
  readonly text: string;
}) {
  return (
    <div className="min-w-0">
      <p className="text-caption text-gray-600">{label}</p>
      <div
        data-testid={testId}
        className="mt-0.5 max-h-60 overflow-y-auto whitespace-pre-wrap rounded-md border border-gray-200 bg-gray-50 p-2.5 text-gray-800 text-small leading-relaxed"
      >
        {text}
      </div>
    </div>
  );
}
